import { EMBLEM } from "@common/utils/emblem";
import { isMarketId, marketPictureUrl } from "@common/platform/market";
import { isStoreItemId, STORE_LIMITS, type StoreItemView, type StorePublicView } from "@common/platform/store";
import { Texture } from "pixi.js";
import { SoundManager } from "../managers/soundManager";

/**
 * The store's catalogue as the game page knows it (0.92): every item (one taken off the store may still be worn), its
 * picture or its clip, from the game server. Read when the page loads, when the store opens, when a match starts, and
 * again when a match shows an item it doesn't know yet (added since) or a picture that can't be read (its file replaced
 * since), at most once every 20 s.
 */

let view: StorePublicView | undefined;
let loading: Promise<StorePublicView | undefined> | undefined;
let loadedAt = 0;
/** the last read failed (no answer, an error, or not a catalogue) */
let failed = false;
const listeners = new Set<(view: StorePublicView) => void>();

export const StoreCatalog = {
    get view(): StorePublicView | undefined {
        return view;
    },

    /**
     * the catalogue couldn't be read, the last time it was, and isn't being read now (0.94 audit: the store then said
     * "Loading the store…" for ever: it says so, and offers to try again)
     */
    get failed(): boolean {
        return failed && loading === undefined;
    },

    item(id: string): StoreItemView | undefined {
        return view?.items.find(item => item.id === id);
    },

    /** told each time the catalogue is read */
    onChange(listener: (view: StorePublicView) => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    /** the catalogue, read now (or the read on its way) */
    load(): Promise<StorePublicView | undefined> {
        loading ??= (async() => {
            // (failed unless it's read)
            failed = true;
            try {
                const response = await fetch(`${API_URL}/store`, { credentials: "include", cache: "no-store" });
                if (!response.ok) return view;
                const answer = (await response.json()) as StorePublicView;
                if (!Array.isArray(answer?.items)) return view;
                view = answer;
                loadedAt = Date.now();
                failed = false;
                for (const listener of listeners) listener(answer);
                return view;
            } catch {
                return view;
            } finally {
                loading = undefined;
            }
        })();
        return loading;
    },

    /** an item by its id, the catalogue read again when it isn't known yet (at most every 20 s) */
    async find(id: string): Promise<StoreItemView | undefined> {
        if (!isStoreItemId(id)) return undefined;
        const known = this.item(id);
        if (known) return known;
        if (Date.now() - loadedAt < 20_000 && view) return undefined;
        await this.load();
        return this.item(id);
    },

    /** the catalogue read again unless it was a moment ago (20 s): whether it was */
    async reloadIfOld(): Promise<boolean> {
        if (view && Date.now() - loadedAt < 20_000) return false;
        await this.load();
        return true;
    }
};

/** the pictures drawn, by their file's address (a file replaced is another picture) */
const pictures = new Map<string, Promise<Texture | undefined>>();

/**
 * A picture emblem as the uniform shows it: drawn in the emblem's circle (the drawn emblems' canvas and clip), one
 * texture per picture (a match shows at most 30 players). One whose file can't be read is looked for again in the
 * catalogue read again (its file replaced since it was read: 0.93 review)
 */
export async function storeEmblemTexture(id: string): Promise<Texture | undefined> {
    // (a flea market listing a player bought, 0.98: its picture, drawn from the seller's design by the server)
    if (isMarketId(id)) return pictureTexture(marketPictureUrl(id));
    const item = await StoreCatalog.find(id);
    if (item?.kind !== "emblem") return undefined;
    const texture = await pictureTexture(item.url);
    if (texture || !(await StoreCatalog.reloadIfOld())) return texture;
    const again = StoreCatalog.item(id);
    return again?.kind === "emblem" && again.url !== item.url ? pictureTexture(again.url) : undefined;
}

/**
 * A picture emblem's picture itself (the end screens draw the soldier on a canvas, 1.05): a flea market listing's from
 * its picture, a store item's from the catalogue. Nothing when it isn't an emblem or can't be read
 */
export async function storeEmblemImage(id: string): Promise<HTMLImageElement | undefined> {
    const url = isMarketId(id)
        ? marketPictureUrl(id)
        : await StoreCatalog.find(id).then(item => (item?.kind === "emblem" ? item.url : undefined));
    if (!url) return undefined;
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    try {
        await image.decode();
    } catch {
        return undefined;
    }
    return image;
}

function pictureTexture(url: string): Promise<Texture | undefined> {
    let known = pictures.get(url);
    if (known) return known;
    known = (async() => {
        const image = new Image();
        image.decoding = "async";
        image.src = url;
        try {
            await image.decode();
        } catch {
            return undefined;
        }
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = EMBLEM.size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return undefined;
        const size = EMBLEM.size;
        // (the circle the drawn emblems keep to: the body)
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size * 0.47, 0, Math.PI * 2);
        ctx.clip();
        // the picture fills the circle, kept in proportion and centred
        const scale = Math.max(size / image.naturalWidth, size / image.naturalHeight);
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;
        ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
        ctx.restore();
        const texture = Texture.from(canvas, true);
        texture.source.autoGarbageCollect = true;
        return texture;
    })();
    pictures.set(url, known);
    // (one that couldn't be read is tried again the next time it's shown)
    void known.then(texture => {
        if (!texture) pictures.delete(url);
    });
    while (pictures.size > 64) pictures.delete(pictures.keys().next().value as string);
    return known;
}

/** each reaction clip being read, by its sound's name (whether it was) */
const clips = new Map<string, Promise<boolean>>();

/** a clip's read given up after this long (read again at the next reaction) */
const CLIP_READ_MS = 8_000;
/** whether bytes start as an MP3 (its tag, or a frame) or an OGG does: the files the store serves */
const soundBytes = (bytes: ArrayBuffer): boolean => {
    const head = new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength));
    return (head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33)
        || (head[0] === 0xff && ((head[1] ?? 0) & 0xe0) === 0xe0)
        || (head[0] === 0x4f && head[1] === 0x67 && head[2] === 0x67 && head[3] === 0x53);
};
/**
 * a reaction's clip read, and added to the sounds, once: one that couldn't be read is read again the next time it's
 * said. The sounds' own reading never said that it failed: every later play of that clip waited for it, and the clip
 * stayed silent for as long as the page was open (0.94 audit)
 */
const clipRead = (sound: string, url: string): Promise<boolean> => {
    if (SoundManager.has(sound)) return Promise.resolve(true);
    let known = clips.get(sound);
    if (known) return known;
    known = (async() => {
        try {
            // (a read that never ends is given up, read again the next time: every later reaction waited on it, silent)
            const response = await fetch(url, typeof AbortSignal.timeout === "function" ? { signal: AbortSignal.timeout(CLIP_READ_MS) } : {});
            if (!response.ok) return false;
            const bytes = await response.arrayBuffer();
            // (a sound's own bytes, as the store took them: a page of a proxy's, answered 200, was added as the clip and
            // never read again, every play failing; the review of the fix)
            if (!soundBytes(bytes)) return false;
            if (!SoundManager.has(sound)) SoundManager.addSound(sound, { source: bytes });
            return true;
        } catch {
            return false;
        }
    })();
    clips.set(sound, known);
    void known.then(read => {
        if (!read) clips.delete(sound);
    });
    return known;
};

/**
 * a reaction as a match says it: its words, and its clip (the sound's name to play once it's read; undefined when it
 * couldn't be, read again the next time)
 */
export async function storeReaction(id: string): Promise<{ readonly text: string, readonly clip: Promise<string | undefined> } | undefined> {
    const item = await StoreCatalog.find(id);
    if (item?.kind !== "reaction") return undefined;
    const sound = `store_reaction_${item.id}_${item.url.split("/").pop()?.split(".")[0] ?? ""}`;
    return { text: item.text ?? item.name, clip: clipRead(sound, item.url).then(read => (read ? sound : undefined)) };
}

/** how long a reaction's words stay over the player (its clip is cut after STORE_LIMITS.maxClipMs) */
export const REACTION_SHOWN_MS = 2600;
export const REACTION_CLIP_MS = STORE_LIMITS.maxClipMs;
